import { opportunityData } from "configs/opportunity.config";
import { states } from "configs/states.config";
import { lazy, Suspense, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { MapController } from "services/map/MapController";
import {
  Dropdown,
  FilePicker,
  ImageModal,
  Input,
  PhotoGallery,
  Textarea,
} from "stories";
import { RadioGroupList } from "stories/components/radioGroup/RadioGroup";
import { useFetchState } from "utils/customHooks/useFetchState";
import { v4 as uuidv4 } from "uuid";
import classes from "./CreateOpportunityDetails.module.scss";

const Map = lazy(() => import("stories/components/map/Map"));

export const imageModel = {
  cover: false,
  description: "",
  label: "",
  filename: "",
};

const CreateOpportunityDetails = () => {
  const form = useFormContext();
  const {
    fields: images,
    append: addImage,
    remove: removeImage,
  } = useFieldArray({
    control: form.control,
    name: "images",
  });
  const handleImagesChange = (images) => {
    form.setValue("images", images);
  };
  const mapController = MapController();
  const [fetchState, sendRequest] = useFetchState();
  const [isModalOpen, setModalOpen] = useState(-1);
  const selectedCountry = useWatch({
    control: form.control,
    name: "opportunity_details.country",
  });
  useEffect(() => {
    if (fetchState?.data?.geometry?.coordinates)
      form.setValue(
        "opportunity_details.google_map_url",
        `http://www.google.com/maps/place/${fetchState?.data?.geometry?.coordinates
          .reverse()
          .join(",")}`
      );
  }, [fetchState.data]); // eslint-disable-line
  const handleGeocode = (e) => {
    const value = e.target.value;
    sendRequest(mapController.geocoder, value);
  };
  const openModal = (index) => {
    setModalOpen(index);
  };
  const handleSelectedFiles = (files) => {
    Array.from(files).forEach((filename) => {
      addImage({
        key: uuidv4(),
        ...imageModel,
        filename,
      });
    });
    openModal(images.length + files.length - 1);
  };

  return (
    <>
      <Row>
        <Col>
          <Input
            label={"Name of Project"}
            placeholder={"Input Here"}
            required
            {...form.register("opportunity_details.project_name")}
          />
        </Col>
        <Col>
          <Controller
            control={form.control}
            name="opportunity_details.project_purpose"
            render={({ field }) => {
              return (
                <RadioGroupList
                  label={"Project Purpose"}
                  list={[
                    { id: 0, title: "For Sale", value: "sale" },
                    { id: 1, title: "For Lease", value: "lease" },
                  ]}
                  {...field}
                />
              );
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Input
            label={"Address"}
            placeholder={"Input Here"}
            required
            {...form.register("opportunity_details.address", {
              onBlur: (e) => handleGeocode(e),
            })}
          />
        </Col>
        <Col />
      </Row>
      <Row>
        <Col>
          <Dropdown
            label={"Company Country"}
            placeholder={"Select"}
            fullWidth
            list={opportunityData.companyCountryList}
            flat
            required
            {...form.register("opportunity_details.country", {
              required: true,
              onChange: (e) => {
                if (e.target.value !== selectedCountry)
                  form.setValue("opportunity_details.state_or_province", "");
              },
            })}
          />
        </Col>
        <Col>
          <Dropdown
            label={"State/Province"}
            placeholder={"Select"}
            fullWidth
            list={selectedCountry ? states[selectedCountry] : undefined}
            flat
            required
            {...form.register("opportunity_details.state_or_province", {
              required: true,
            })}
          />
        </Col>
        <Col>
          <Input
            label={"City"}
            placeholder={"Input Here"}
            required
            {...form.register("opportunity_details.city", { required: true })}
          />
        </Col>
        <Col>
          <Input
            label={"Postal Code"}
            placeholder={"Input Here"}
            required
            {...form.register("opportunity_details.postal_code", {
              required: true,
            })}
          />
        </Col>
      </Row>
      <Row className={"mt-3"}>
        <Col>
          <div className={classes["map"]}>
            <Suspense fallback="Loading...">
              <Map coordinates={fetchState?.data?.center} />
            </Suspense>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className="mt-4">
          <Dropdown
            label={"Visibility"}
            placeholder={"Input Here"}
            list={opportunityData.projectVisibility}
            fullWidth
            flat
            {...form.register("opportunity_details.visibility")}
          />
        </Col>
        <Col />
      </Row>
      <Row>
        <Col>
          <Textarea
            label={"Project Highlights"}
            placeholder={"Input Here"}
            required
            {...form.register("opportunity_details.project_highlights")}
          />
        </Col>
        <Col />
      </Row>
      <Row>
        <Col>
          {!imageModel.visible && images.length > 0 ? (
            <div className={"mt-4"}>
              <div className={"label-text mb-2"}>Upload Images</div>
              <PhotoGallery
                imageList={images}
                removeImageHandler={removeImage}
                uploadImageHandler={handleSelectedFiles}
                onSelectImage={openModal}
              />
            </div>
          ) : (
            <FilePicker
              onSelect={handleSelectedFiles}
              label="Upload Images"
              type="image"
              multiple={true}
            />
          )}
        </Col>
        <Col />
      </Row>
      <ImageModal
        visibility={isModalOpen}
        setVisibility={() => setModalOpen(-1)}
        files={images}
        onAccept={handleImagesChange}
      />
    </>
  );
};

export default CreateOpportunityDetails;
